import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { clearSessionData } from '../utils/sessionCleanup';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

// Versión de la app (se usa para invalidar cache cuando hay actualización)
const APP_VERSION = process.env.REACT_APP_VERSION || '8.1.10';

// Cache TTL para el documento de usuario. Deliberadamente corto: mientras la
// caché es válida NO se relee Firestore, así que una degradación de rol o una
// inhabilitación tardan ese tiempo en surtir efecto. Antes era 1 año.
const USER_CACHE_TTL = 8 * 60 * 60 * 1000; // 8 horas (una jornada escolar)

// Migas para pintar la cáscara antes de que Firebase Auth responda. Es la misma
// información y el mismo TTL que la caché de usuario, pero con una clave que se
// puede leer sin conocer el uid: sin esto el arranque muestra el overlay de
// sesión hasta que onAuthStateChanged resuelve. Se purga al cerrar sesión.
const SESION_ARRANQUE_KEY = `sesion_ultima_v${APP_VERSION}`;

// Los cuatro roles que ven la pantalla de Auditoría, y por tanto los únicos a los
// que les interesa la salud de la auditoría.
const ROLES_CON_AUDITORIA = ['admin', 'superadmin', 'directivo', 'administrativo'];

function leerSesionDeArranque() {
  try {
    const raw = window.localStorage.getItem(SESION_ARRANQUE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s || !s.uid || !s.email) return null;
    if (s.disabled === true || !s.cachedAt) return null;
    if ((Date.now() - s.cachedAt) >= USER_CACHE_TTL) return null;
    return {
      uid: s.uid,
      email: s.email,
      role: s.role ?? null,
      nombre: s.nombre ?? null,
      canCopyClasses: s.canCopyClasses ?? false,
      theme: s.theme ?? 'light',
      cachedAt: s.cachedAt,
      docId: s.docId || s.email
    };
  } catch (err) {
    return null;
  }
}

function guardarSesionDeArranque(datos) {
  try {
    if (!datos || !datos.uid || !datos.email) return;
    window.localStorage.setItem(SESION_ARRANQUE_KEY, JSON.stringify({
      uid: datos.uid,
      email: datos.email,
      role: datos.role ?? null,
      nombre: datos.nombre ?? null,
      canCopyClasses: datos.canCopyClasses ?? false,
      theme: datos.theme ?? 'light',
      cachedAt: datos.cachedAt || Date.now(),
      docId: datos.docId || datos.email,
      disabled: false
    }));
  } catch (err) {
    // Sin espacio o modo privado: no es crítico, el arranque cae al overlay.
  }
}

export function AuthProvider({ children }) {
  // Con sesión cacheada fresca, la app pinta de inmediato (loading arranca en
  // false) y la reconciliación con Auth pasa a segundo plano.
  const [sesionDeArranque] = useState(() => leerSesionDeArranque());
  const [user, setUser] = useState(sesionDeArranque);
  const [loading, setLoading] = useState(!sesionDeArranque);

  // Salud de la auditoría: cuántas escrituras de auditoría fallaron. Es un dato de la
  // INSTITUCIÓN, no del usuario, así que se lee una sola vez por sesión y solo para
  // quien puede ver la pantalla de Auditoría. Es un indicador —no un registro— y por
  // eso no bloquea nada ni avisa en el momento: lo pinta esa pantalla, y nada más.
  const [auditSalud, setAuditSalud] = useState(null);

  useEffect(() => {
    if (!ROLES_CON_AUDITORIA.includes(user?.role)) {
      setAuditSalud(null);
      return;
    }
    let cancelado = false;
    getDoc(doc(db, 'auditoria_salud', 'actual'))
      .then((snap) => { if (!cancelado) setAuditSalud(snap.exists() ? snap.data() : null); })
      .catch(() => { if (!cancelado) setAuditSalud(null); });
    return () => { cancelado = true; };
  }, [user?.role, user?.uid]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const userEmail = firebaseUser.email?.toLowerCase();
        const cacheKey = `user_doc_v${APP_VERSION}_${userEmail}_${firebaseUser.uid}`;

        const fetchAndCacheUser = async (force = false) => {
          try {
            if (!force) {
              const raw = window.localStorage.getItem(cacheKey);
              if (raw) {
                const cached = JSON.parse(raw);
                if (cached && cached.cachedAt && (Date.now() - cached.cachedAt) < USER_CACHE_TTL) {
                  // Un usuario inhabilitado no debe entrar ni con caché válida
                  if (cached.disabled === true) {
                    await auth.signOut();
                    setLoading(false);
                    return;
                  }
                  const sesion = {
                    uid: firebaseUser.uid,
                    email: userEmail,
                    role: cached.role ?? null,
                    nombre: cached.nombre ?? null,
                    canCopyClasses: cached.canCopyClasses ?? false,
                    theme: cached.theme ?? 'light',
                    cachedAt: cached.cachedAt,
                    docId: cached.docId || userEmail // Fallback razonable
                  };
                  setUser(sesion);
                  guardarSesionDeArranque(sesion);
                  setLoading(false);
                  return;
                }
              }
            }

            // Lectura fresca de Firestore
            let docId = userEmail;
            let userSnap = await getDoc(doc(db, 'users', docId));
            if (!userSnap.exists()) {
              docId = firebaseUser.uid;
              userSnap = await getDoc(doc(db, 'users', docId));
            }

            const data = userSnap.exists() ? userSnap.data() : {};

            // El alta del primer superadmin se hace sembrando el documento en
            // Firestore (ver INSTALL.md). No se auto-provisiona desde el cliente:
            // las reglas exigen rol de administrador para crear en "users", así
            // que el intento fallaría, y dejaba el email maestro en el bundle.
            if (!userSnap.exists()) {
              await auth.signOut();
              setLoading(false);
              return;
            }

            // Un usuario inhabilitado pierde el acceso, aunque su cuenta de
            // Firebase Auth siga existiendo.
            if (data.disabled === true) {
              await auth.signOut();
              setLoading(false);
              return;
            }

            const role = data.role ?? null;
            const nombre = data.nombre ?? null;
            const canCopyClasses = data.canCopyClasses ?? false;
            let theme = data.theme;
            if (!theme && typeof window !== 'undefined' && window.matchMedia) {
              theme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
            } else if (!theme) {
              theme = 'light';
            }
            const now = Date.now();

            const userData = {
              uid: firebaseUser.uid,
              email: userEmail,
              role,
              nombre,
              canCopyClasses,
              theme,
              cachedAt: now,
              docId: docId
            };

            setUser(userData);
            guardarSesionDeArranque(userData);
            window.localStorage.setItem(cacheKey, JSON.stringify({
              role,
              nombre,
              canCopyClasses,
              theme,
              cachedAt: now,
              docId: docId,
              disabled: false
            }));
            window.localStorage.setItem('userTheme', theme); // Para inyección sincrónica en index.html
            setLoading(false);
          } catch (err) {
            setUser({ uid: firebaseUser.uid, email: firebaseUser.email, role: null, nombre: null, canCopyClasses: false, theme: 'light', cachedAt: 0, docId: userEmail });
            setLoading(false);
          }
        };

        fetchAndCacheUser();
        // Exponer la función de refresco
        window.__refreshUser = () => fetchAndCacheUser(true);

      } else {
        setUser(null);
        setLoading(false);
        delete window.__refreshUser;
        // Sin sesión no debe quedar nada que permita pintar la cáscara.
        window.localStorage.removeItem(SESION_ARRANQUE_KEY);
        // Al cerrar sesión se eliminan los datos del dispositivo: el padrón de
        // alumnos, sus asistencias y sus anécdotas quedarían legibles sin
        // credenciales en un equipo compartido.
        clearSessionData().catch(() => { });
      }
    });
    return unsubscribe;
  }, []);

  const refreshUser = React.useCallback(() => {
    if (window.__refreshUser) window.__refreshUser();
  }, []);

  const updateUserTheme = React.useCallback(async (newTheme) => {
    if (!user || !user.docId) return;
    try {
      // Optimistic update
      setUser(prev => ({ ...prev, theme: newTheme }));

      const { updateDoc } = await import('firebase/firestore');
      await updateDoc(doc(db, 'users', user.docId), { theme: newTheme });

      // Update cache
      const userEmail = user.email?.toLowerCase();
      const cacheKey = `user_doc_v${APP_VERSION}_${userEmail}_${user.uid}`;
      const raw = window.localStorage.getItem(cacheKey);
      if (raw) {
        const cached = JSON.parse(raw);
        cached.theme = newTheme;
        window.localStorage.setItem(cacheKey, JSON.stringify(cached));
      }
      window.localStorage.setItem('userTheme', newTheme); // Para inyección sincrónica en index.html
      const sesionRaw = window.localStorage.getItem(SESION_ARRANQUE_KEY);
      if (sesionRaw) {
        const s = JSON.parse(sesionRaw);
        s.theme = newTheme;
        window.localStorage.setItem(SESION_ARRANQUE_KEY, JSON.stringify(s));
      }
    } catch (err) {
      console.error("Error updating user theme", err);
      // Revert optimistic update
      refreshUser();
    }
  }, [user, refreshUser]);

  const value = React.useMemo(() => ({ user, loading, refreshUser, updateUserTheme, auditSalud }), [user, loading, refreshUser, updateUserTheme, auditSalud]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
